import { atLeast, type Role, type SendingWorkflow, type TextRefusalCode, type TextStatus } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * Texts we send (ADR-098 in platform): the words for where one stands, and why one may not leave.
 *
 * core-api decides what may leave. A send it refuses is a 409 whose body carries the reason as a
 * sentence (`detail`) and as a code (`code`), and the compose routes give the same two before
 * anything is typed. The sentence is core-api's and is shown as it is; the code is for the composer
 * to act on: `quiet_hours` carries `until`, `number_cannot_text` and `no_number` are an admin's to
 * fix, `dnc` and `opted_out` are final. Pure, for `test/texts.test.ts`.
 */

export type TextTone = "neutral" | "accent" | "success" | "danger";

/** Where a text we sent stands, as a badge says it. */
const SENT_STATUS: Record<string, { label: string; tone: TextTone }> = {
  // Kept for the person's 8 am (ACL-630): not gone yet.
  scheduled: { label: "Scheduled", tone: "neutral" },
  queued: { label: "Sending", tone: "neutral" },
  sent: { label: "Sent", tone: "accent" },
  delivered: { label: "Delivered", tone: "success" },
  failed: { label: "Not delivered", tone: "danger" },
};

/** Why a scheduled text waits (ACL-630): texts go out between 8 am and 9 pm on the contact's own
 *  clock, and one the system sent outside them goes at their 8 am. */
export const SCHEDULED_WHY = "Outside 8 am–9 pm on the contact's clock, so it waits for their 8 am.";

/** When a scheduled text goes out, on the reader's own clock as the composer says quiet hours'
 *  end: "Scheduled for Oct 29, 2026, 11:00 AM". */
export function scheduledFor(sendAt: string | null | undefined, when: (iso: string) => string = formatDateTime): string {
  return sendAt ? `Scheduled for ${when(sendAt)}` : "Scheduled";
}

/** Who sent a text nobody on the team sent by hand: the workflow, by name (ACL-385). */
export function workflowSender(workflow: SendingWorkflow): string {
  return `Workflow: ${workflow.name}`;
}

/** Whether the reader is told which workflow or cadence did what nobody on the team did by hand
 *  (ACL-591): a supervisor and up. A rep reads the text, the note or the move and when it happened;
 *  the automation behind it is internal, and core-api keeps it for whoever runs the workflows. */
export function seesAutomation(role: Role | undefined): boolean {
  return atLeast(role, "supervisor");
}

/** Who sent a text or wrote a note when nobody on the team did, as the reader is told it: the
 *  workflow by name for whoever sees automation, "Automated" for everyone else. */
export function automatedBy(workflow: SendingWorkflow | null | undefined, sees: boolean): string {
  return sees && workflow ? workflowSender(workflow) : "Automated";
}

/** The badge for a text we sent; null for a text we received, which has no delivery to report. */
export function sentStatusText(status: TextStatus | string | undefined): { label: string; tone: TextTone } | null {
  return SENT_STATUS[status ?? ""] ?? null;
}

export interface TextRefusal {
  code: TextRefusalCode;
  /** The reason as a sentence, as core-api wrote it. */
  detail: string;
  /** For `quiet_hours`: when the person's 8 am comes round. */
  until: string | null;
}

/** Why a send was refused, from the 409 core-api answered (an `ApiError`: its status, its message
 *  and the body it came with); null for any other error. */
export function textRefusal(error: unknown): TextRefusal | null {
  if (!(error instanceof Error)) return null;
  const failure = error as Error & { status?: unknown; body?: unknown };
  if (failure.status !== 409) return null;
  const body = failure.body as { code?: unknown; until?: unknown } | null | undefined;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code as TextRefusalCode,
    detail: failure.message,
    until: typeof body.until === "string" ? body.until : null,
  };
}

/** What a send that did not answer with its text says about it: `refused` when nothing was kept,
 *  with why; `unconfirmed` when nobody can tell. */
export type SendFailure = { kind: "refused"; detail: string } | { kind: "unconfirmed" };

/**
 * Whether a failed send was kept. A 4xx is core-api turning the request down before it kept
 * anything: a 409 with its sentence (`textRefusal`), or a refusal of the request itself (an ended
 * session, a lead the sender may not open). Anything else, a 5xx from core-api or the proxy in front
 * of it, or a request that got no answer at all, says nothing either way: core-api keeps the text
 * before it calls the provider, so it may be in the thread and on its way, and sending it again would
 * be a second text.
 */
export function sendFailure(error: unknown): SendFailure {
  const refusal = textRefusal(error);
  if (refusal) return { kind: "refused", detail: refusal.detail };
  if (error instanceof Error) {
    const status = (error as Error & { status?: unknown }).status;
    if (typeof status === "number" && status >= 400 && status < 500) return { kind: "refused", detail: error.message };
  }
  return { kind: "unconfirmed" };
}
